import {
  Component,
  HostListener,
  OnInit,
  ChangeDetectionStrategy,
  input,
  InputSignal,
  computed,
  output,
  inject,
} from '@angular/core';
import { CommonModule, DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import {
  PanelConfigI,
  ProccessedCoassessCase,
  ProccessedCoreactCase,
} from '../../interfaces';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumber, InputNumberInputEvent } from 'primeng/inputnumber';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { PanelComponent } from '../panel/panel.component';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '@ngx-translate/core';

import { Roles } from '../../utils';
@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss',
  standalone: true,

  imports: [
    FormsModule,
    PanelModule,
    InputTextModule,
    InputNumber,
    SelectModule,
    DatePickerModule,
    ButtonModule,
    NgClass,
    PanelComponent,
    TranslatePipe,
    CommonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterComponent implements OnInit {
  translateService = inject(TranslationService);

  cases: InputSignal<(ProccessedCoassessCase | ProccessedCoreactCase)[]> =
    input.required();
  currentUserRole: InputSignal<string> = input.required();
  filterConfig: InputSignal<any> = input.required();

  appliedRows = output<(ProccessedCoassessCase | ProccessedCoreactCase)[]>();


  totalCasesCount = computed(() => this.cases().length);

  closedCasesCount = computed(
    () =>
      this.cases().filter(
        (invR: any) =>
          invR.investigationResults?.toLowerCase() === 'approved' ||
          invR.investigationResults?.toLowerCase() === 'rejected'
      ).length
  );
  casesThisMonthCount = computed(() => {
    const date = new Date();
    const currentMonth = date.getMonth();
    return this.cases().filter((invR: any) => {
      const started = invR.started ? new Date(invR.started) : null;
      if (!started || Number.isNaN(started.getTime())) {
        return false;
      }

      const month = started.getMonth();
      return currentMonth === month;
    }).length;
  });

  averageCasesPerDay = computed(() => {
    const date = new Date();
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const today = date.getDate();

    const casesThisMonth = this.cases().filter((invR: any) => {
      const started = invR.started ? new Date(invR.started) : null;
      if (!started || Number.isNaN(started.getTime())) {
        return false;
      }

      return (
        started.getFullYear() === currentYear &&
        started.getMonth() === currentMonth
      );
    });

    const daysElapsed = today;
    const average = daysElapsed > 0 ? casesThisMonth.length / daysElapsed : 0;

    return average;
  });

  roles = Roles;

  expanded: any = {};
  timeout: any;
  private temp: (ProccessedCoassessCase | ProccessedCoreactCase)[] = [];

  investigationResult: string = 'all';

  selectedRangeDates: Date[] | undefined;
  currentDay: string = '';
  showFilters: boolean = false;
  showStatistics: boolean = true;
  systemResultOptions: { label: string; value: string }[] = [];
  claimSeverityOptions: { label: string; value: string }[] = [];

  statisticPanelConfig: PanelConfigI = {
    toggleable: true,
    collapsed: false,
    expandIcon: undefined,
    collapseIcon: undefined,
    headerIcon: 'fa-solid fa-chart-line',
    headerLabel: 'CLAIMS.FILTERS.STATISTICS_HEADER',
  };
  filterPanelConfig: PanelConfigI = {
    toggleable: true,
    collapsed: true,
    expandIcon: undefined,
    collapseIcon: undefined,
    headerIcon: 'fa-solid fa-filter',
    headerLabel: 'CLAIMS.FILTERS.FILTERS_HEADER',
  };


  readonly cfg = computed(() => this.filterConfig?.() ?? ({} as any));
  readonly firstRow = computed(() => (this.cfg().statistics ?? []).slice(0, 4));
  readonly secondRow = computed(() => (this.cfg().statistics ?? []).slice(4, 7));

  // hospital admissions local state
  private selectedAdmissionId: string = '';
  private selectedReceivedDate?: Date;
  private selectedPatientName: string = '';
  private selectedHospital: string = '';
  private selectedStatus: string = '';

  private selectedSystemResult: string = '';
  private selectedId: string = '';
  private selectedClaimSeverity: string = '';
  private selectedMinimumPriority: number = 0;
  private selectedMaximumPriority: number = 0;
  langChangeSub: any;

  ngOnInit(): void {
    this.temp = this.cases();
    this.loadFilterOptions();
    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.loadFilterOptions();
    });
  }

  loadFilterOptions(): void {
    this.systemResultOptions = [
      {
        label: this.translateService.getTranslation('CLAIMS.FILTERS.SUSPICIOUS'),
        value: 'suspicious',
      },
      {
        label: this.translateService.getTranslation('CLAIMS.FILTERS.NOT_SUSPICIOUS'),
        value: 'notsuspicious',
      },
    ];

    // this.claimSeverityOptions = [
    //   {
    //     label: this.translateService.getTranslation('CLAIMS.FILTERS.HIGH'),
    //     value: 'high',
    //   },
    //   {
    //     label: this.translateService.getTranslation('CLAIMS.FILTERS.MEDIUM'),
    //     value: 'medium',
    //   },
    //   {
    //     label: this.translateService.getTranslation('CLAIMS.FILTERS.LOW'),
    //     value: 'low',
    //   },
    // ];
    this.claimSeverityOptions = [
      {
        label: "Καλύπτεται",
        value: 'Covered',
      },
      {
        label: "Δεν καλύπτεται",
        value: 'Not Covered',
      },
      {
        label: "Αγνωστο Συμβόλαιο",
        value: 'Contract Unknown',
      },
      {
        label: "Με επιφύλαξη",
        value: 'Reservation of Rights',
      },
    ];
  }
  resetRows() {
    this.resetFields();
    this.temp = this.cases();
    this.appliedRows.emit(this.temp);
  }

  apply() {
    this.temp = this.cases();

    // claims-side (gated)
    if (this.cfg()?.showClaimSeverityFilter) this.updateFilterByClaimSeverity();
    if (this.cfg()?.showDateRange) this.updateFilterByDateRange();
    if (this.cfg()?.showCaseIdFilter) this.updateFilterById();
    if (this.cfg()?.showSystemResultFilter) this.updateFilterBySystemResult();
    if (this.cfg()?.showPriorityIndexFilter) this.updateFilterByPriorityIndex();

    // admissions-side (gated)
    if (this.cfg()?.showId) this.updateFilterByAdmissionId();
    if (this.cfg()?.showReceivedDate) this.updateFilterByReceivedDate();
    if (this.cfg()?.showPatientName) this.updateFilterByPatientName();
    if (this.cfg()?.showHospital) this.updateFilterByHospital();
    if (this.cfg()?.showStatus) this.updateFilterByStatus();

    this.appliedRows.emit(this.temp);
  }
  onSystemResultFilterChange(event: SelectChangeEvent) {
    this.selectedSystemResult = event.value;
  }
  onIdFilterChange(event: InputNumberInputEvent) {
    // const value = selectElement.value;
    this.selectedId = String(event.value) ?? '';
  }
  onClaimSeverityFilterChange(event: SelectChangeEvent) {
    this.selectedClaimSeverity = event.value;
  }
  onMinimumPriorityIndexChange(event: InputNumberInputEvent) {
    this.selectedMinimumPriority = Number(event.value) ?? '';
  }
  onMaximumPriorityIndexChange(event: InputNumberInputEvent) {
    this.selectedMaximumPriority = Number(event.value) ?? '';
  }

  onSelectedAdmissionIdFilterChange(event: InputNumberInputEvent) {
    this.selectedAdmissionId = String(event.value) ?? '';
  }

  onSelectedPatientNameFilterChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.selectedPatientName = (target?.value ?? '').trim();
  }

  onSelectedHospitalFilterChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.selectedHospital = (target?.value ?? '').trim();
  }

  onSelectedStatusFilterChange(event: SelectChangeEvent) {
    this.selectedStatus = event.value;
  }

  /*Listens for key press  and updates the result*/
  @HostListener('document:keyup', ['$event'])
  handleEnterKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.apply();
    }
  }

  toggleInvestigationResult(result: string) {
    if (this.investigationResult === result) {
      this.resetInvestigationResult();
    } else {
      this.investigationResult = result;
      this.applyInvestigationResultFilter();
    }
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  toggleStatistics() {
    this.showStatistics = !this.showStatistics;
  }

  // private applyInvestigationResultFilter() {
  //   this.temp = this.cases();
  //   if (this.investigationResult != 'all') {
  //     this.temp = this.cases().filter((d: any) => {
  //       return (
  //         d.investigationResults.toLowerCase() ===
  //         this.investigationResult.toLowerCase()
  //       );
  //     });
  //     this.appliedRows.emit(this.temp);
  //   } else {
  //     this.resetInvestigationResult();
  //   }
  // }
  private resetInvestigationResult() {
    this.investigationResult = 'all';
    this.temp = this.cases();
    this.appliedRows.emit(this.temp);
  }

  private resetFields() {
    let selectTag = document.querySelectorAll('select');
    selectTag.forEach((element) => {
      element.value = '';
    });

    const id = document.querySelector('input[name="id"]') as HTMLInputElement;
    id.value = 'Filter by ID...';

    const fromDateInput = document.querySelector(
      'input[name="fromDate"]'
    ) as HTMLInputElement;
    const toDateInput = document.querySelector(
      'input[name="toDate"]'
    ) as HTMLInputElement;

    fromDateInput.value = '';
    toDateInput.value = '';

    const maxPriority = document.querySelector(
      'input[name="priorityIndexMin"]'
    ) as HTMLInputElement;
    maxPriority.value = 'Minimum priority index value...';
    const minPriority = document.querySelector(
      'input[name="priorityIndexMax"]'
    ) as HTMLInputElement;
    minPriority.value = 'Maximum priority index value...';
  }
  private updateFilterByClaimSeverity() {
    if (this.selectedClaimSeverity) {
      this.temp = this.temp.filter((d: any) => {
        return (
          d.caseSeverity.toLowerCase() ===
          this.selectedClaimSeverity.toLowerCase()
        );
      });
    }
  }

  private updateFilterByInvestigationResult() {
    const investigationResultSelect = document.querySelector(
      'select[name="investigationResult"]'
    ) as HTMLSelectElement;
    if (investigationResultSelect) {
      const selectedResult = investigationResultSelect.value.toLowerCase();
      if (selectedResult !== '') {
        this.temp = this.cases().filter((d: any) => {
          return d.investigationResults.toLowerCase() === selectedResult;
        });
      }
    }
  }

  private updateFilterByDateRange() {
    const fromDateInput = document.querySelector(
      'input[name="fromDate"]'
    ) as HTMLInputElement;
    const toDateInput = document.querySelector(
      'input[name="toDate"]'
    ) as HTMLInputElement;

    const fromDateVal = new Date(fromDateInput.value);
    const toDateVal = new Date(toDateInput.value);
    if (fromDateInput.value && toDateInput.value) {
      this.temp = this.temp.filter((d: { started: Date | null }) => {
        if (!d.started) {
          return false;
        }

        const caseDate = new Date(d.started);
        if (Number.isNaN(caseDate.getTime())) {
          return false;
        }

        return caseDate >= fromDateVal && caseDate <= toDateVal;
      });
    }
  }

  private updateFilterById() {
    if (this.selectedId && this.selectedId.length === 4) {
      this.temp = this.temp.filter(
        (d: { caseId: any }) => Number(d.caseId) === Number(this.selectedId)
      );
    }
  }
  private updateFilterBySystemResult() {
    if (this.selectedSystemResult) {
      this.temp = this.temp.filter((d: any) => {
        d.systemResult = d.systemResult.split(' ').join('');
        return d.systemResult.toLowerCase() === this.selectedSystemResult;
      });
    }
  }

  private updateFilterByPriorityIndex() {
    const minPriority = this.selectedMinimumPriority;
    const maxPriority = this.selectedMaximumPriority;

    if (minPriority < maxPriority) {
      this.temp = this.temp.filter((d: { caseSeverity: any }) => {
        const caseSeverity = Number(d.caseSeverity);
        const result =
          caseSeverity * 100 >= minPriority &&
          caseSeverity * 100 <= maxPriority;

        return result;
      });
    } else if (minPriority && !maxPriority) {
      this.temp = this.temp.filter((d: { caseSeverity: any }) => {
        const caseSeverity = Number(d.caseSeverity);
        const result = caseSeverity * 100 >= minPriority;
        return result;
      });
    }
  }

  private updateFilterByAdmissionId() {
    const cfg = this.cfg();
    if (!cfg?.idKey || !this.selectedAdmissionId) return;
    this.temp = this.temp.filter((row: any) =>
      String(row[cfg.idKey] ?? '').toLowerCase() === String(this.selectedAdmissionId).toLowerCase()
    );
  }

  private updateFilterByReceivedDate() {
    const cfg = this.cfg();
    if (!cfg?.receivedDateKey || !this.selectedReceivedDate) return;
    const target = new Date(this.selectedReceivedDate);
    this.temp = this.temp.filter((row: any) => {
      const d = new Date(row[cfg.receivedDateKey]);
      return d.getFullYear() === target.getFullYear() &&
        d.getMonth() === target.getMonth() &&
        d.getDate() === target.getDate();
    });
  }

  private updateFilterByPatientName() {
    const cfg = this.cfg();
    if (!cfg?.patientNameKey || !this.selectedPatientName) return;
    const q = this.selectedPatientName.toLowerCase();
    this.temp = this.temp.filter((row: any) =>
      String(row[cfg.patientNameKey] ?? '').toLowerCase().includes(q)
    );
  }

  private updateFilterByHospital() {
    const cfg = this.cfg();
    if (!cfg?.hospitalKey || !this.selectedHospital) return;
    const q = this.selectedHospital.toLowerCase();
    this.temp = this.temp.filter((row: any) =>
      String(row[cfg.hospitalKey] ?? '').toLowerCase().includes(q)
    );
  }

  private updateFilterByStatus() {
    const cfg = this.cfg();
    if (!cfg?.statusKey || !this.selectedStatus) return;
    const q = this.selectedStatus.toLowerCase();
    this.temp = this.temp.filter((row: any) =>
      String(row[cfg.statusKey] ?? '').toLowerCase() === q
    );
  }

  private normalizeStatus(s: string): string {
    // makes 'UNDER_INVESTIGATION' and 'Under investigation' comparable
    return String(s ?? '').replace(/[_\s]+/g, ' ').trim().toLowerCase();
  }

  get visibleInvestigationButtons() {
    const cfg = this.cfg();
    const isAdmin = this.currentUserRole() === this.roles.Administrator;

    // support either `investigationButtons` or `quickStatusButtons`
    const configured = (cfg?.investigationButtons ?? cfg?.quickStatusButtons) as string[] | undefined;

    const defaultSet = ['all', 'approved', 'rejected', 'under investigation', ...(isAdmin ? ['unassigned'] : [])];
    const allowed = configured?.length ? configured : defaultSet;

    const labels: Record<string, string> = {
      'all': cfg?.allLabel ?? (cfg?.showId ? 'All Admissions' : 'All Cases'),
      'approved': 'Approved',
      'rejected': 'Rejected',
      'under investigation': 'Under Investigation',
      'unassigned': 'Unassigned',
    };

    // hide "unassigned" for non-admins even if someone passes it
    return allowed
      .filter(k => k !== 'unassigned' || isAdmin)
      .map(k => ({ key: this.normalizeStatus(k), label: labels[this.normalizeStatus(k)] ?? k }));
  }

  // (optional) make filtering robust against underscores/spaces
  private applyInvestigationResultFilter() {
    this.temp = this.cases();
    if (this.investigationResult !== 'all') {
      const wanted = this.normalizeStatus(this.investigationResult);
      this.temp = this.cases().filter((d: any) =>
        this.normalizeStatus(d.investigationResults) === wanted
      );
      this.appliedRows.emit(this.temp);
    } else {
      this.resetInvestigationResult();
    }
  }

}
